// ============================================================================
// CLEO'S SECRET — CleoIntake
// File: src/pages/CleoIntake.jsx
//
// The AI conversation between patient and Cleo, before their GP appointment.
// THE most emotionally charged surface in the platform — it must feel calm,
// private, and considered. Not a chatbot UI. A conversation.
//
// Pattern: full-bleed PAP with quiet SAND header chrome at the top.
// The chat itself is the page. Cormorant for the conversation body,
// Cinzel for the small chrome (eyebrow, progress, labels), bronze italic
// for emphasis. Streaming pulse, voice input, and completion handoff all
// preserved exactly.
//
// Preserved logic:
//   • /api/cleo-intake streaming SSE protocol (chunk / done / error)
//   • Web Speech API voice input (en-AU) when supported
//   • onComplete handoff with { summary, intakeId, email }
//   • 12-step progress meter
//   • Streaming caret + thinking dots
//   • Enter to send, Shift+Enter for newline
//   • Auto-scroll to bottom, focus return after streaming
// ============================================================================

import React, { useState, useRef, useEffect } from 'react';
import {
  FF, FS, P,
} from '../lib/design-tokens';
import Ankh from '../components/Ankh';

// ───────── Opening message ─────────
function getOpening(firstName) {
  var greeting = firstName ? 'Hello ' + firstName + ',' : 'Hello,';
  return (
    greeting + ' I am Cleo, and I am here to help your GP understand you before your first appointment.\n\n' +
    'This is a relaxed conversation. There are no right or wrong answers, and everything you share stays between you and your clinical team.\n\n' +
    'You can type, or tap the microphone to speak. Whichever feels more comfortable.\n\n' +
    'What brought you to Cleo\u2019s Secret? What has been going on that made you feel it was time to seek some answers?'
  );
}

// ═════════════════════════════════════════════════════════════════════════
// CleoIntake
// ═════════════════════════════════════════════════════════════════════════
export default function CleoIntake(props) {
  var onNavigate = props.onNavigate;
  var onComplete = props.onComplete;
  var email = props.email;
  var firstName = props.firstName;

  var messagesState = useState([{ role: 'assistant', content: getOpening(firstName) }]);
  var messages = messagesState[0];
  var setMessages = messagesState[1];

  var inputState = useState('');
  var input = inputState[0];
  var setInput = inputState[1];

  var loadingState = useState(false);
  var loading = loadingState[0];
  var setLoading = loadingState[1];

  var streamingState = useState(false);
  var streaming = streamingState[0];
  var setStreaming = streamingState[1];

  var completeState = useState(false);
  var complete = completeState[0];
  var setComplete = completeState[1];

  var errorState = useState(null);
  var error = errorState[0];
  var setError = errorState[1];

  var listeningState = useState(false);
  var listening = listeningState[0];
  var setListening = listeningState[1];

  var voiceSupportedState = useState(false);
  var voiceSupported = voiceSupportedState[0];
  var setVoiceSupported = voiceSupportedState[1];

  var thinkingDotsState = useState(0);
  var thinkingDots = thinkingDotsState[0];
  var setThinkingDots = thinkingDotsState[1];

  var scrollRef = useRef(null);
  var inputRef = useRef(null);
  var recognitionRef = useRef(null);
  var thinkingIntervalRef = useRef(null);

  // ───────── Web Speech API setup ─────────
  useEffect(function() {
    var SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (SR) {
      setVoiceSupported(true);
      var recognition = new SR();
      recognition.lang = 'en-AU';
      recognition.interimResults = true;
      recognition.continuous = false;
      recognition.onresult = function(event) {
        var transcript = '';
        for (var i = 0; i < event.results.length; i++) {
          transcript += event.results[i][0].transcript;
        }
        setInput(transcript);
        if (event.results[event.results.length - 1].isFinal) {
          setListening(false);
        }
      };
      recognition.onerror = function() { setListening(false); };
      recognition.onend = function() { setListening(false); };
      recognitionRef.current = recognition;
    }
  }, []);

  // ───────── Auto-scroll on new content ─────────
  useEffect(function() {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, loading, streaming]);

  // ───────── Return focus after streaming ─────────
  useEffect(function() {
    if (!loading && !streaming && inputRef.current) {
      inputRef.current.focus();
    }
  }, [loading, streaming]);

  // ───────── Thinking dots animation ─────────
  useEffect(function() {
    if (loading && !streaming) {
      thinkingIntervalRef.current = setInterval(function() {
        setThinkingDots(function(d) { return (d + 1) % 4; });
      }, 400);
    } else {
      if (thinkingIntervalRef.current) clearInterval(thinkingIntervalRef.current);
      setThinkingDots(0);
    }
    return function() {
      if (thinkingIntervalRef.current) clearInterval(thinkingIntervalRef.current);
    };
  }, [loading, streaming]);

  function toggleVoice() {
    if (!recognitionRef.current) return;
    if (listening) {
      recognitionRef.current.stop();
      setListening(false);
    } else {
      setInput('');
      recognitionRef.current.start();
      setListening(true);
    }
  }

  function sendMessage() {
    if (!input.trim() || loading || streaming || complete) return;
    var userMsg = input.trim();
    setInput('');
    setError(null);
    if (listening && recognitionRef.current) {
      recognitionRef.current.stop();
      setListening(false);
    }

    var updated = messages.concat([{ role: 'user', content: userMsg }]);
    setMessages(updated);
    setLoading(true);

    fetch('/api/cleo-intake', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        messages: updated.map(function(m) { return { role: m.role, content: m.content }; }),
        email: email,
      }),
    })
      .then(function(response) {
        if (!response.ok) {
          return response.text().then(function() { throw new Error('Request failed: ' + response.status); });
        }

        setMessages(function(prev) { return prev.concat([{ role: 'assistant', content: '' }]); });
        setLoading(false);
        setStreaming(true);

        var reader = response.body.getReader();
        var decoder = new TextDecoder();
        var buffer = '';
        var streamedText = '';

        function processChunk() {
          return reader.read().then(function(result) {
            if (result.done) return;

            buffer += decoder.decode(result.value, { stream: true });
            var lines = buffer.split('\n');
            buffer = lines.pop() || '';

            var currentEvent = null;
            for (var i = 0; i < lines.length; i++) {
              var line = lines[i].trim();
              if (line === '') continue;
              if (line.startsWith('event: ')) {
                currentEvent = line.slice(7);
              } else if (line.startsWith('data: ')) {
                var dataStr = line.slice(6);
                try {
                  var data = JSON.parse(dataStr);

                  if (currentEvent === 'chunk' && data.text) {
                    streamedText += data.text;
                    setMessages(function(prev) {
                      var u = prev.slice();
                      u[u.length - 1] = { role: 'assistant', content: streamedText };
                      return u;
                    });
                  } else if (currentEvent === 'done') {
                    if (data.fullText) {
                      setMessages(function(prev) {
                        var u = prev.slice();
                        u[u.length - 1] = { role: 'assistant', content: data.fullText };
                        return u;
                      });
                    }
                    if (data.conversationComplete && data.clinicalSummary) {
                      setComplete(true);
                      if (onComplete) {
                        onComplete({
                          summary: data.clinicalSummary,
                          intakeId: data.intakeId,
                          email: email,
                        });
                      }
                    }
                  } else if (currentEvent === 'error') {
                    throw new Error(data.message || data.error || 'Stream error');
                  }
                } catch (parseErr) {
                  console.error('Parse error:', parseErr, dataStr);
                }
              }
            }

            return processChunk();
          });
        }

        return processChunk();
      })
      .catch(function(err) {
        setError(err.message);
        setMessages(function(prev) {
          var last = prev[prev.length - 1];
          if (last && last.role === 'assistant' && last.content === '') {
            var u = prev.slice(0, -1);
            return u.concat([{ role: 'assistant', content: 'I am sorry, I seem to be having a technical moment. Could you try sending that again?' }]);
          }
          return prev.concat([{ role: 'assistant', content: 'I am sorry, I seem to be having a technical moment. Could you try sending that again?' }]);
        });
      })
      .finally(function() {
        setLoading(false);
        setStreaming(false);
      });
  }

  function handleKeyDown(e) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  }

  var userCount = messages.filter(function(m) { return m.role === 'user'; }).length;

  // ────────────────────────────────────────────────────────────────────────
  // Inline components (closures over palette + state)
  // ────────────────────────────────────────────────────────────────────────

  // Avatar — quiet gold-ringed monogram, not a corporate chatbot puck.
  function Avatar() {
    return (
      <div style={{
        width: 38,
        height: 38,
        borderRadius: '50%',
        background: 'rgba(201, 169, 97, 0.08)',
        border: '1px solid ' + P.GO,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        marginRight: 14,
        marginTop: 2,
      }}>
        <span style={{
          fontFamily: FF,
          fontSize: 18,
          fontStyle: 'italic',
          fontWeight: 500,
          color: P.BR,
          lineHeight: 1,
        }}>C</span>
      </div>
    );
  }

  return (
    <div style={{
      fontFamily: FF,
      background: P.PAP,
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      width: '100%',
    }}>

      {/* ═════════ Header chrome (sticky) ═════════ */}
      <header style={{
        background: P.SAND,
        borderBottom: '1px solid ' + P.SAND3,
        padding: 'clamp(18px, 2.2vw, 26px) clamp(20px, 3vw, 40px)',
        textAlign: 'center',
        position: 'sticky',
        top: 0,
        zIndex: 10,
      }}>
        <div style={{
          fontFamily: FF,
          fontSize: 'clamp(10px, 0.9vw, 12px)',
          fontWeight: 700,
          letterSpacing: '0.36em',
          textTransform: 'uppercase',
          color: P.BR2,
          marginBottom: 8,
        }}>Cleo's Secret</div>
        <h1 style={{
          fontFamily: FF,
          fontSize: 'clamp(22px, 2.4vw, 30px)',
          fontWeight: 400,
          color: P.INK,
          lineHeight: 1.2,
          letterSpacing: '-0.008em',
          margin: 0,
        }}>Your <span style={{ fontStyle: 'italic', color: P.BR2 }}>conversation</span> with Cleo.</h1>
        <div style={{
          fontFamily: FF,
          fontSize: 'clamp(11px, 0.95vw, 13px)',
          color: P.BR2,
          marginTop: 8,
          letterSpacing: '0.04em',
          fontStyle: 'italic',
        }}>Private and confidential</div>

        {/* Progress meter — 12 segment ruler, gold fills as conversation deepens */}
        {!complete && userCount > 0 && (
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            gap: 5,
            marginTop: 16,
          }}>
            {Array.from({ length: 12 }).map(function(_, i) {
              var filled = i < userCount;
              return (
                <div key={i} style={{
                  width: filled ? 28 : 12,
                  height: 2,
                  background: filled ? P.GO : P.SAND3,
                  transition: 'all 0.4s ease',
                }} />
              );
            })}
          </div>
        )}
      </header>

      {/* ═════════ Conversation scroll area ═════════ */}
      <div
        ref={scrollRef}
        style={{
          flex: 1,
          overflowY: 'auto',
          padding: 'clamp(28px, 3.4vw, 48px) clamp(20px, 8vw, 120px) clamp(20px, 2.4vw, 32px)',
          maxWidth: 1100,
          width: '100%',
          margin: '0 auto',
          boxSizing: 'border-box',
        }}>

        {messages.map(function(msg, i) {
          var isUser = msg.role === 'user';
          var isAssistant = !isUser;
          var isFirstAssistant = isAssistant && i === 0;
          var isLastAssistant = isAssistant && i === messages.length - 1 && streaming;

          // First assistant message rendered as an editorial premise (italic, bronze)
          // for the opening invitation. Subsequent assistant messages are calm prose.
          if (isFirstAssistant) {
            return (
              <div key={i} style={{
                display: 'flex',
                alignItems: 'flex-start',
                marginBottom: 'clamp(28px, 3vw, 40px)',
              }}>
                <Avatar />
                <div style={{
                  flex: 1,
                  padding: 'clamp(20px, 2.4vw, 28px) clamp(22px, 2.6vw, 32px)',
                  background: P.PAP,
                  border: '1px solid ' + P.SAND3,
                  borderLeft: '3px solid ' + P.GO,
                  maxWidth: 760,
                }}>
                  <div style={{
                    fontFamily: FF,
                    fontSize: 'clamp(16px, 1.4vw, 19px)',
                    color: P.TX,
                    lineHeight: 1.85,
                    whiteSpace: 'pre-wrap',
                  }}>{msg.content}</div>
                  {isLastAssistant && msg.content.length > 0 && (
                    <span style={{
                      display: 'inline-block',
                      width: 2,
                      height: 22,
                      background: P.GO,
                      marginLeft: 2,
                      verticalAlign: 'middle',
                      animation: 'cs-blink 1s infinite',
                    }} />
                  )}
                </div>
              </div>
            );
          }

          return (
            <div key={i} style={{
              display: 'flex',
              justifyContent: isUser ? 'flex-end' : 'flex-start',
              alignItems: 'flex-start',
              marginBottom: 'clamp(18px, 2vw, 24px)',
            }}>
              {!isUser && <Avatar />}
              <div style={{
                maxWidth: '70%',
                padding: 'clamp(14px, 1.6vw, 18px) clamp(18px, 2vw, 22px)',
                fontFamily: FF,
                fontSize: 'clamp(15px, 1.3vw, 17px)',
                lineHeight: 1.75,
                color: isUser ? P.INK : P.TX,
                background: isUser ? 'rgba(201, 169, 97, 0.08)' : P.PAP,
                border: '1px solid ' + (isUser ? P.GO : P.SAND3),
                whiteSpace: 'pre-wrap',
              }}>
                {msg.content}
                {isLastAssistant && msg.content.length > 0 && (
                  <span style={{
                    display: 'inline-block',
                    width: 2,
                    height: 20,
                    background: P.GO,
                    marginLeft: 2,
                    verticalAlign: 'middle',
                    animation: 'cs-blink 1s infinite',
                  }} />
                )}
              </div>
            </div>
          );
        })}

        {/* Thinking indicator */}
        {loading && !streaming && (
          <div style={{
            display: 'flex',
            alignItems: 'flex-start',
            marginBottom: 'clamp(18px, 2vw, 24px)',
          }}>
            <Avatar />
            <div style={{
              padding: 'clamp(14px, 1.6vw, 18px) clamp(18px, 2vw, 22px)',
              background: P.PAP,
              border: '1px solid ' + P.SAND3,
              fontFamily: FF,
              fontSize: 'clamp(15px, 1.3vw, 17px)',
              color: P.TF,
              fontStyle: 'italic',
              display: 'flex',
              alignItems: 'center',
              gap: 10,
            }}>
              <span>Cleo is thinking</span>
              <span style={{ display: 'inline-flex', gap: 4 }}>
                <span style={{
                  width: 5, height: 5, borderRadius: '50%',
                  background: P.GO,
                  opacity: thinkingDots >= 1 ? 1 : 0.25,
                  transition: 'opacity 0.2s',
                }} />
                <span style={{
                  width: 5, height: 5, borderRadius: '50%',
                  background: P.GO,
                  opacity: thinkingDots >= 2 ? 1 : 0.25,
                  transition: 'opacity 0.2s',
                }} />
                <span style={{
                  width: 5, height: 5, borderRadius: '50%',
                  background: P.GO,
                  opacity: thinkingDots >= 3 ? 1 : 0.25,
                  transition: 'opacity 0.2s',
                }} />
              </span>
            </div>
          </div>
        )}

        {/* Completion state */}
        {complete && (
          <div style={{
            margin: 'clamp(32px, 4vw, 56px) auto 0',
            maxWidth: 720,
            padding: 'clamp(32px, 4vw, 56px) clamp(28px, 3.4vw, 48px)',
            background: 'rgba(201, 169, 97, 0.06)',
            border: '1px solid ' + P.GO,
            borderLeft: '3px solid ' + P.GO,
            textAlign: 'center',
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              marginBottom: 'clamp(20px, 2.4vw, 28px)',
            }}>
              <Ankh responsive="closing" color={P.GO} />
            </div>

            <div style={{
              fontFamily: FF,
              fontSize: 'clamp(11px, 0.95vw, 13px)',
              fontWeight: 700,
              letterSpacing: '0.36em',
              textTransform: 'uppercase',
              color: P.BR2,
              marginBottom: 18,
            }}>Conversation complete</div>

            <p style={{
              fontFamily: FF,
              fontSize: 'clamp(22px, 2.4vw, 32px)',
              fontStyle: 'italic',
              fontWeight: 400,
              color: P.BR2,
              lineHeight: 1.4,
              letterSpacing: '-0.005em',
              marginBottom: 'clamp(18px, 2vw, 24px)',
            }}>Thank you for sharing.</p>

            <p style={{
              fontFamily: FF,
              fontSize: 'clamp(15px, 1.3vw, 17px)',
              color: P.TX,
              lineHeight: 1.85,
              marginBottom: 8,
            }}>Your GP will review everything before your appointment.</p>

            <p style={{
              fontFamily: FF,
              fontSize: 'clamp(15px, 1.3vw, 17px)',
              color: P.TL,
              lineHeight: 1.85,
              marginBottom: 'clamp(28px, 3vw, 40px)',
            }}>A personalised preparation pack has been prepared based on your conversation.</p>

            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 12,
              alignItems: 'center',
            }}>
              <button
                onClick={function() { if (onNavigate) onNavigate('preppack'); }}
                style={{
                  fontFamily: FS,
                  fontSize: 'clamp(11px, 0.95vw, 12px)',
                  fontWeight: 600,
                  letterSpacing: '0.28em',
                  textTransform: 'uppercase',
                  padding: '14px 36px',
                  background: P.INK,
                  color: P.PAP,
                  border: 'none',
                  cursor: 'pointer',
                  width: '100%',
                  maxWidth: 320,
                  boxSizing: 'border-box',
                  transition: 'background 0.2s',
                }}
                onMouseEnter={function(e) { e.currentTarget.style.background = P.BR; }}
                onMouseLeave={function(e) { e.currentTarget.style.background = P.INK; }}>
                View your Cleo prep pack
              </button>
              <button
                onClick={function() { if (onNavigate) onNavigate('scheduler'); }}
                style={{
                  fontFamily: FS,
                  fontSize: 'clamp(11px, 0.95vw, 12px)',
                  fontWeight: 600,
                  letterSpacing: '0.28em',
                  textTransform: 'uppercase',
                  padding: '14px 36px',
                  background: 'transparent',
                  color: P.BR2,
                  border: '1px solid ' + P.GO,
                  cursor: 'pointer',
                  width: '100%',
                  maxWidth: 320,
                  boxSizing: 'border-box',
                  transition: 'background 0.2s',
                }}
                onMouseEnter={function(e) { e.currentTarget.style.background = 'rgba(201, 169, 97, 0.08)'; }}
                onMouseLeave={function(e) { e.currentTarget.style.background = 'transparent'; }}>
                Book your consultation
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ═════════ Composer (hidden when complete) ═════════ */}
      {!complete && (
        <div style={{
          padding: 'clamp(16px, 2vw, 24px) clamp(20px, 8vw, 120px) clamp(20px, 2.6vw, 32px)',
          borderTop: '1px solid ' + P.SAND3,
          background: P.PAP,
          maxWidth: 1100,
          width: '100%',
          margin: '0 auto',
          boxSizing: 'border-box',
        }}>
          {error && (
            <div style={{
              fontFamily: FF,
              fontSize: 'clamp(13px, 1.1vw, 14px)',
              color: P.RED,
              fontStyle: 'italic',
              marginBottom: 10,
            }}>{error}</div>
          )}

          <div style={{
            display: 'flex',
            gap: 12,
            alignItems: 'flex-end',
          }}>
            {voiceSupported && (
              <button
                onClick={toggleVoice}
                title={listening ? 'Stop listening' : 'Speak to Cleo'}
                style={{
                  width: 50,
                  height: 50,
                  flexShrink: 0,
                  border: '1px solid ' + (listening ? P.RED : P.GO),
                  background: listening ? P.RED_BG : P.PAP,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.2s',
                }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
                     stroke={listening ? P.RED : P.BR}
                     strokeWidth="1.6"
                     strokeLinecap="round"
                     strokeLinejoin="round">
                  <rect x="9" y="1" width="6" height="12" rx="3" />
                  <path d="M19 10v1a7 7 0 01-14 0v-1" />
                  <line x1="12" y1="19" x2="12" y2="23" />
                  <line x1="8" y1="23" x2="16" y2="23" />
                </svg>
              </button>
            )}

            <textarea
              ref={inputRef}
              value={input}
              onChange={function(e) { setInput(e.target.value); }}
              onKeyDown={handleKeyDown}
              placeholder={listening ? 'Listening\u2026' : 'Share what is on your mind\u2026'}
              rows={2}
              disabled={loading || streaming}
              style={{
                flex: 1,
                padding: '14px 18px',
                fontFamily: FF,
                fontSize: 'clamp(15px, 1.3vw, 17px)',
                color: P.INK,
                background: P.PAP,
                border: '1px solid ' + (listening ? P.RED : P.SAND3),
                outline: 'none',
                resize: 'none',
                lineHeight: 1.6,
                opacity: loading || streaming ? 0.55 : 1,
                boxSizing: 'border-box',
                transition: 'border-color 0.2s',
              }}
              onFocus={function(e) {
                if (!listening) e.currentTarget.style.borderColor = P.BR;
              }}
              onBlur={function(e) {
                if (!listening) e.currentTarget.style.borderColor = P.SAND3;
              }}
            />

            <button
              onClick={sendMessage}
              disabled={!input.trim() || loading || streaming}
              style={{
                fontFamily: FS,
                fontSize: 'clamp(11px, 0.95vw, 12px)',
                fontWeight: 600,
                letterSpacing: '0.28em',
                textTransform: 'uppercase',
                padding: '0 28px',
                height: 50,
                background: input.trim() && !loading && !streaming ? P.INK : 'rgba(26,15,8,0.25)',
                color: P.PAP,
                border: 'none',
                cursor: input.trim() && !loading && !streaming ? 'pointer' : 'default',
                whiteSpace: 'nowrap',
                transition: 'background 0.2s',
              }}>Send</button>
          </div>

          {listening && (
            <div style={{
              fontFamily: FF,
              fontSize: 'clamp(12px, 1vw, 13px)',
              color: P.RED,
              fontStyle: 'italic',
              textAlign: 'center',
              marginTop: 10,
            }}>Listening. Speak naturally, then tap the microphone to stop.</div>
          )}
          {!listening && !error && (
            <div style={{
              fontFamily: FS,
              fontSize: 'clamp(11px, 0.9vw, 12px)',
              color: P.TL,
              textAlign: 'center',
              marginTop: 12,
              letterSpacing: '0.04em',
              fontStyle: 'italic',
            }}>
              {voiceSupported
                ? 'Type, or tap the microphone to speak.'
                : 'Type your response and press send.'}
            </div>
          )}
        </div>
      )}

      <style>{`
        @keyframes cs-blink {
          0%, 50% { opacity: 1; }
          51%, 100% { opacity: 0; }
        }
      `}</style>

    </div>
  );
}
